import { html, property, state } from 'lit-element';
import { repeat } from 'lit-html/directives/repeat';
import {
  event,
  EventEmitter,
  KatLitElement,
  register,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import { KatToast } from './toast';
import styles from './toast-group.lit.scss';
import { ToastManager } from './toast-manager';

export interface Toast {
  description: string;
  id: string;
  variant?: 'info' | 'warning' | 'danger' | 'success';
  persistent?: boolean;
  actionText?: string;
  ctaClicked?: (toast: Toast) => void;
  toastClosed?: (toast: Toast) => void;
  toastOpened?: (toast: Toast) => void;
}

const MAX_NUMBER_OF_TOASTS = 5;

/**
 * @component {kat-toast-group} KatToastGroup Renders toasts in a FIFO queue. A toast is a non-disruptive message that provides quick, at-a-glance feedback on the outcome of an action. It is used to convey general confirmation or actions that aren’t critical. Toasts will auto-dismiss after 5 seconds unless specified as persistent.
 * @guideline Do Create the KatToastGroup near the root of your application. <strong>This component is a beta component and might be updated with breaking changes to its interface.</strong>
 * @guideline Dont Create more than one KatToastGroup.
 * @status Production
 * @requires Toast:../es/toast-group
 * @theme flo
 * @example Default {"script":"setTimeout((()=>{const t=document.querySelector(\".playground-preview-component\");let demo;if (t.querySelector('.demo')) {demo = t.querySelector('.demo')} else {console.log('did not find demo');demo = document.createElement('div');demo.classList.add('demo');t.appendChild(demo);};demo?(demo.innerHTML=\"<kat-toast-group></kat-toast-group><kat-button id='api-show' style='margin-right: 10px'>Toast</kat-button><kat-button id='api-show-persist'>Persistent Toast</kat-button><style>.demo-buttons{display:flex;flex-direction:column;overflow:hidden}.demo-buttons button{white-space:nowrap}html{height:100%}.katal{display:flex;height:calc(100% - 32px);flex-direction:column-reverse}.dock{position:absolute;right:0;display:flex;flex-direction:column}</style>\",setTimeout((()=>{let t=[\"success\",\"warning\",\"info\",\"danger\"],e=0;const o=document.querySelector(\"kat-toast-group\"),i=document.querySelector(\"#api-show-persist\"),n=document.querySelector(\"#api-show\");o&&i&&n?(n.addEventListener(\"click\",(()=>{e++,o.openToast({description:\" Lorem ipsum dolor sit amet, consectetur adipiscing elit \",actionText:\"Click Here\",variant:t[e%4],persistent:!1,id:e,guid:\"api-test\"+e})})),i.addEventListener(\"click\",(()=>{e++,o.openToast({description:\" Lorem ipsum dolor sit amet, consectetur adipiscing elit \",actionText:\"Click Here\",variant:t[e%4],id:e,persistent:!0,guid:\"api-test\"+e})}))):console.warn(\"missing something: \",o,i,n)}),1500)):console.warn(\"no playground\")}),1500);"}
 */
@register('kat-toast-group')
export class KatToastGroup extends KatLitElement {
  // #region TYPE METADATA
  /**
   * @typedef {Object} Toast
   * @type {object}
   * @property {string} description Required.  The text that will be displayed.
   * @property {string} id Required. The id of the toast. Must be unique.
   * @property {boolean} persistent Optional. If true, toast will not auto-dismiss. If false, user needs to click X to dismiss it.
   * @property {string} actionText Optional. The text to display in the CTA link.
   * @property {(t:Toast)=>void} ctaClicked Optional. The callback that fires when a cta is clicked.
   * @property {(t:Toast)=>void} toastClosed Optional. The callback that fires when a toast is closed.
   * @property {(t:Toast)=>void} toastOpened Optional. The callback that fires when a toast is opened.
   */
  // #endregion

  static get styles() {
    return [baseStyles, styles];
  }

  /** The list of new toasts to display. Do not include toasts that have previously been shown because they will be duplicated.*/
  @property({ attribute: false })
  toasts: Toast[] = [];

  /**  Fires when the user clicks the cta. Only happens if `action-text` property is provided. */
  @event('ctaClicked', { bubbles: true, composed: true })
  _ctaClickedEventEmitter: EventEmitter<{ toast?: Toast }>;

  /**  Fires when the toast is dismissed. */
  @event('toastClosed', { bubbles: true, composed: true })
  _toastClosedEventEmitter: EventEmitter<{ toast?: Toast }>;

  /**  Fires when the toast is opened. */
  @event('toastOpened', { bubbles: true, composed: true })
  _toastOpenedEventEmitter: EventEmitter<{ toast?: Toast }>;

  @state()
  toastManager: ToastManager = new ToastManager();

  /** The toast content to be rendered. */
  @state()
  openedToasts: KatToast[] = [];

  /** Holds the kat-toast elements that are moved to the exiting-animation-space */
  @state()
  closingToasts: Record<string, KatToast> = {};

  /** internal tracker used to control first toast animations*/
  @state()
  private toastsWereAdded: boolean;

  /**
   *  API Method to show a toast. Only 5 toasts can be shown at a time. After 5 are shown, the first toast will be removed when a new toast is added.
   *
   * @param toast
   *
   * @katalmethod
   */
  public openToast(toast: Toast): KatToast {
    const newToast = new KatToast(toast);
    newToast._toastGroup = this;
    this.toastManager.addToast(newToast);
    newToast.classList.add('new-toast');
    newToast.addEventListener(
      'animationend',
      e => {
        if (e.animationName === 'fadeIn') {
          newToast.classList.remove('new-toast');
        }
      },
      { once: true }
    );
    this.openedToasts = this.toastManager.getToasts();
    this._open(newToast);
    return newToast;
  }

  /**
   * API Method to close a toast.
   * @param toast The id or KatToast to be closed
   *
   * @katalmethod
   */
  public closeToast(toast: KatToast | string) {
    let t;
    if (toast instanceof KatToast) {
      t = toast;
    } else {
      t = this.toastManager.getToast(toast);
    }

    if (t) {
      t._dismissToast();
    }
  }

  /**
   * Called only internally and by KatToast.
   * @param t The Toast to be closed
   */
  _hide(t: KatToast) {
    const toast = this.toastManager.getToast(t.id);
    if (!toast) {
      return;
    }
    this._measureOutgoingToast(toast.id);
    toast.style.top = toast.parentElement?.offsetTop + 'px';
    toast.style.left = toast.parentElement?.offsetLeft + 'px';
    toast.classList.add('closing');
    this.closingToasts[toast.id] = toast;
    this.toastManager.removeToast(toast.id).then(id => {
      delete this.closingToasts[id];
      this.openedToasts = this.toastManager.getToasts();
    });
    this.openedToasts = this.toastManager.getToasts();
  }

  private _measureOutgoingToast(id: string) {
    const outgoing = this.shadowRoot.querySelector(`kat-toast[id="${id}"]`);
    if (outgoing) {
      /** Toasts can have dynamic heights and therefore the shrinking-spacer needs to know far to shrink from*/
      this.style.setProperty(
        '--outgoing-toast-height',
        `${outgoing.clientHeight}px`
      );
    }
  }

  private async _open(toast: KatToast) {
    if (toast) {
      const { dismissalType } = await toast._open();
      toast.classList.remove('opened');
      if (dismissalType === 'expired') {
        this._hide(toast);
      }
    }
  }

  render() {
    const closingToasts = repeat(
      Object.values(this.closingToasts),
      toast => toast.id,
      toast => html` ${toast} `
    );

    const toasts = repeat(
      this.openedToasts,
      toast => toast.id,
      toast => {
        // Wrap the toast with an expanding-spacer div
        if (toast instanceof KatToast) {
          return html`<div
            class="expanding-spacer"
            id="${'expanding-spacer-' + toast.id}"
          >
            ${toast}
          </div>`;
        }
        // Don't wrap any shrinking-spacers
        return html`${toast}`;
      }
    );

    return html`
      <div class="toast-group">
        ${toasts}
        <div class="exiting-animation-space">${closingToasts}</div>
      </div>
    `;
  }

  updated(changedProps) {
    // When openedToasts change, track whether it was a toast addition or toast removal
    // If an addition, measure the new toast and set the css property for the animation
    // If too many toasts exist, close all the toasts that exceed 5 (starting with oldest)
    if (changedProps.has('openedToasts')) {
      const oldToasts = changedProps.get('openedToasts') || [];
      const newToasts = this.openedToasts || [];
      if (oldToasts.length > newToasts.length) {
        this.toastsWereAdded = false;
      } else {
        this.toastsWereAdded = true;
        this.openedToasts.forEach(toast => {
          requestAnimationFrame(() => {
            const expandingDiv = this.shadowRoot.querySelector(
              `#${'expanding-spacer-' + toast.id}`
            ) as HTMLDivElement;
            if (expandingDiv) {
              /** Toasts can have dynamic heights and therefore the expanding-spacer needs to know far to grow */
              expandingDiv.style.setProperty(
                '--incoming-toast-height',
                `${toast.clientHeight}px`
              );
            }
          });
        });
      }
      if (this.openedToasts.length > MAX_NUMBER_OF_TOASTS) {
        for (let i = MAX_NUMBER_OF_TOASTS; i < this.openedToasts.length; i++) {
          this.closeToast(this.openedToasts[i]);
        }
      }
    }
    // Only render toasts that are not currently showing
    if (changedProps.has('toasts')) {
      this.toasts
        .filter(t => !this.toastManager.hasToast(t.id))
        .forEach(t => this.openToast(t));
    }
  }
}
